import { useMemo } from 'react';

const flattenRows = (rows, prefix = 'row') => {
  const result = [];

  const walk = (nodes, parentHierarchy, pfx) => {
    nodes.forEach((node, index) => {
      const id = `${pfx}-${index}`;
      const hierarchy = [...parentHierarchy, node.id];

      result.push({
        id,
        hierarchy,
        isChild: parentHierarchy.length > 0,
        originalData: node,
      });

      if (node.children) {
        walk(node.children, hierarchy, `${id}-child`);
      }
    });
  };

  if (rows) walk(rows, [], prefix);
  return result;
};

const buildProcessedColumns = (columnLabels, renderCell) =>
  columnLabels?.map((label, index) => {
    const base = {
      field: label,
      headerName: label,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
    };

    if (index === 0) {
      return {
        ...base,
        align: 'left',
        cellClassName: 'key-number-campain',
        width: 250,
        minWidth: 250,
        maxWidth: 250,
      };
    }

    if (index === 1) {
      return { ...base, cellClassName: 'previous-campain', renderCell };
    }

    return { ...base, renderCell };
  }) ?? [];

const buildProcessedRows = (flattenedRows, processedColumns) =>
  flattenedRows.map((row) => {
    const entry = { id: row.id, hierarchy: row.hierarchy };
    processedColumns.forEach((col, idx) => {
      entry[col.field] =
        idx === 0 ? row.originalData.id : (row.originalData.data?.[idx - 1]?.value ?? '-');
    });
    return entry;
  });

export const useTreeData = (rows, columnLabels, options = {}) => {
  const { renderCell: renderCellOption } = options;

  return useMemo(() => {
    const flattenedRows = flattenRows(rows);

    const renderCell = renderCellOption
      ? (params, colIndex) => {
          const flatRow = flattenedRows.find((r) => r.id === params.row.id);
          return renderCellOption(params, colIndex, flatRow);
        }
      : undefined;

    const makeCellRenderer = renderCell
      ? (colIndex) => (params) => renderCell(params, colIndex)
      : undefined;

    const processedColumns = buildProcessedColumns(columnLabels, undefined).map((col, idx) =>
      idx > 0 && makeCellRenderer ? { ...col, renderCell: makeCellRenderer(idx) } : col,
    );

    const processedRows = buildProcessedRows(flattenedRows, processedColumns);

    return { processedColumns, processedRows };
  }, [rows, columnLabels, renderCellOption]);
};

export default useTreeData;
